import { useApolloClient } from '@apollo/client';
import { useCallback, useState } from 'react';
import * as accountService from '../services/account';
import { GET_VIEWER_QUERY, ViewerQueryResult } from '../services/account';
import { getSession } from '../services/authentication';
import { Account } from '../services/models/account';
import { AppType } from '../services/models/app-type';
import * as plausible from '../services/plausible';
import * as segment from '../services/segment';
import * as stripeService from '../services/stripe';
import { notifyToast } from '../state/app-state';
import { CheckoutCallbackRoute } from '../utils/Routes';
import { useServiceErrorHandler } from './useServiceErrorHandler';

type AccountPatch = (account: Account) => Account;

// Applies the same targeted merges the retired session/account reducers
// performed, but against the cached getViewer result so every useAccount
// consumer re-renders with the fresh data.
const useViewerCachePatch = () => {
  const client = useApolloClient();

  return useCallback(
    (patch: AccountPatch) => {
      client.cache.updateQuery<ViewerQueryResult>({ query: GET_VIEWER_QUERY }, data =>
        data ? { viewer: { ...data.viewer, account: patch(data.viewer.account) } } : data
      );
    },
    [client]
  );
};

export const useUpdateName = () => {
  const patchAccount = useViewerCachePatch();
  const handleServiceError = useServiceErrorHandler();
  const [isUpdatingName, setIsUpdatingName] = useState(false);

  const updateName = useCallback(
    async (firstName: string, lastName: string) => {
      setIsUpdatingName(true);
      try {
        const updated = await accountService.updateName(firstName, lastName);
        // Same traits the redux-segment middleware emitted on
        // AccountAction.CompleteUpdatingName. Fired unconditionally (from the
        // session, not inside the cache updater, which is skipped on a
        // cache miss).
        const sessionAccount = getSession()?.account;
        if (sessionAccount) {
          segment.identifyTraits(sessionAccount.userid, {
            firstName: updated.firstName,
            lastName: updated.lastName,
          });
        }
        patchAccount(account => ({ ...account, ...updated }));
        notifyToast('Your name has been updated');
      } catch (error) {
        handleServiceError(error);
      } finally {
        setIsUpdatingName(false);
      }
    },
    [patchAccount, handleServiceError]
  );

  return { updateName, isUpdatingName };
};

export const useUpdateEmail = () => {
  const handleServiceError = useServiceErrorHandler();
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);

  const updateEmail = useCallback(
    async (email: string) => {
      setIsUpdatingEmail(true);
      try {
        await accountService.updateEmail(email);
        // Refreshes the viewer (network-only through the shared client, so
        // the cached account picks up the new email) and re-syncs traits.
        await accountService.refreshUserAccount();
        notifyToast('Please check your inbox for your verification email');
      } catch (error) {
        handleServiceError(error);
      } finally {
        setIsUpdatingEmail(false);
      }
    },
    [handleServiceError]
  );

  return { updateEmail, isUpdatingEmail };
};

export const useSendVerificationEmail = () => {
  const handleServiceError = useServiceErrorHandler();
  const [isSendingVerificationEmail, setIsSendingVerificationEmail] = useState(false);

  const sendVerificationEmail = useCallback(async () => {
    setIsSendingVerificationEmail(true);
    try {
      await accountService.sendVerificationEmail();
      notifyToast('Please check your inbox for your verification email');
    } catch (error) {
      handleServiceError(error);
    } finally {
      setIsSendingVerificationEmail(false);
    }
  }, [handleServiceError]);

  return { sendVerificationEmail, isSendingVerificationEmail };
};

export const useUpdateSetting = () => {
  const patchAccount = useViewerCachePatch();
  const handleServiceError = useServiceErrorHandler();
  const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);

  const updateSetting = useCallback(
    async (name: string, value: string) => {
      setIsUpdatingSettings(true);
      try {
        const settings = await accountService.updateSetting(name, value);
        patchAccount(account => ({
          ...account,
          settings: [
            ...(account.settings || []).filter(s => !settings.some(m => m.name === s.name)),
            ...settings,
          ],
        }));
      } catch (error) {
        handleServiceError(error);
      } finally {
        setIsUpdatingSettings(false);
      }
    },
    [patchAccount, handleServiceError]
  );

  return { updateSetting, isUpdatingSettings };
};

export const useUpdateTimezoneSetting = () => {
  const { updateSetting, isUpdatingSettings } = useUpdateSetting();

  const updateTimezoneSetting = useCallback(
    (timeZone: string) => updateSetting('time_zone', timeZone),
    [updateSetting]
  );

  return { updateTimezoneSetting, isUpdatingTimezone: isUpdatingSettings };
};

export const useCancelSubscription = () => {
  const patchAccount = useViewerCachePatch();
  const handleServiceError = useServiceErrorHandler();
  const [isCancelingSubscription, setIsCancelingSubscription] = useState(false);

  // Returns whether the cancellation succeeded so callers can keep their
  // confirm dialog open on failure (the error itself surfaces through the
  // service error funnel).
  const cancelSubscription = useCallback(async (): Promise<boolean> => {
    setIsCancelingSubscription(true);
    try {
      const subscription = await accountService.cancelSubscription();
      patchAccount(account => ({ ...account, subscription }));
      return true;
    } catch (error) {
      handleServiceError(error);
      return false;
    } finally {
      setIsCancelingSubscription(false);
    }
  }, [patchAccount, handleServiceError]);

  return { cancelSubscription, isCancelingSubscription };
};

export const useDeactivateAccount = () => {
  const handleServiceError = useServiceErrorHandler();
  const [isDeactivatingAccount, setIsDeactivatingAccount] = useState(false);

  // Returns whether the deactivation succeeded so callers can tear the
  // session down (the error itself surfaces through the service error
  // funnel).
  const deactivateAccount = useCallback(
    async (reason: string, comment: string): Promise<boolean> => {
      setIsDeactivatingAccount(true);
      try {
        return await accountService.deactivateAccount(reason, comment);
      } catch (error) {
        handleServiceError(error);
        return false;
      } finally {
        setIsDeactivatingAccount(false);
      }
    },
    [handleServiceError]
  );

  return { deactivateAccount, isDeactivatingAccount };
};

export const useGenerateZapierApiKey = () => {
  const patchAccount = useViewerCachePatch();
  const handleServiceError = useServiceErrorHandler();
  const [isGeneratingZapierApiKey, setIsGeneratingZapierApiKey] = useState(false);

  const generateZapierApiKey = useCallback(async () => {
    setIsGeneratingZapierApiKey(true);
    try {
      const apiApp = await accountService.generateZapierApiKey();
      // Replace any existing Zapier key while keeping the other API apps
      // (the legacy reducer filtered with === and dropped them — a bug).
      patchAccount(account => ({
        ...account,
        apiApps: [...(account.apiApps || []).filter(x => x.type !== AppType.Zapier), apiApp],
      }));
    } catch (error) {
      handleServiceError(error);
    } finally {
      setIsGeneratingZapierApiKey(false);
    }
  }, [patchAccount, handleServiceError]);

  return { generateZapierApiKey, isGeneratingZapierApiKey };
};

export const useChangeSubscription = () => {
  const patchAccount = useViewerCachePatch();
  const handleServiceError = useServiceErrorHandler();
  const [isChangingSubscription, setIsChangingSubscription] = useState(false);

  const changeSubscription = useCallback(
    async (planId: string) => {
      setIsChangingSubscription(true);
      try {
        const subscription = await stripeService.changeSubscription(planId);
        patchAccount(account => ({
          ...account,
          subscription: { ...account.subscription, ...subscription },
        }));
      } catch (error) {
        handleServiceError(error);
      } finally {
        setIsChangingSubscription(false);
      }
    },
    [patchAccount, handleServiceError]
  );

  return { changeSubscription, isChangingSubscription };
};

export const useCheckoutFlow = () => {
  const handleServiceError = useServiceErrorHandler();
  const [isStartingCheckoutFlow, setIsStartingCheckoutFlow] = useState(false);

  // Every checkout returns through /treasury/callback: the backend appends
  // ?session_id={CHECKOUT_SESSION_ID} to success_url only (newslit-webapp#918),
  // so the callback page polls for the webhook-driven subscription on success
  // and bounces marker-less abandons straight back to the plan page.
  const initCheckoutFlow = useCallback(
    async (planId: string) => {
      setIsStartingCheckoutFlow(true);
      try {
        // Same event pair (segment + plausible) the legacy thunk emitted
        // around the redirect to Stripe Checkout.
        segment.trackEvent('Checkout Started', { plan: planId });
        plausible.trackEvent('Checkout Started', { plan: planId });
        await stripeService.redirectToPurchaseFlow(
          planId,
          window.location.origin + CheckoutCallbackRoute
        );
        plausible.trackEvent('Checkout Completed', { plan: planId });
        segment.trackEvent('Checkout Completed', { plan: planId });
        setIsStartingCheckoutFlow(false);
      } catch (error) {
        handleServiceError(error, () => setIsStartingCheckoutFlow(false));
      }
    },
    [handleServiceError]
  );

  const initUpdatePaymentSettings = useCallback(
    async (callbackUrl?: string) => {
      try {
        await stripeService.redirectToUpdatePaymentMethodFlow(callbackUrl);
      } catch (error) {
        handleServiceError(error);
      }
    },
    [handleServiceError]
  );

  return { initCheckoutFlow, initUpdatePaymentSettings, isStartingCheckoutFlow };
};
